import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { SketchPicker } from 'react-color';
import { Box, Flex } from "theme-ui";

import { getRegions, createRegion, updateRegion, deleteRegion } from "api/Clients/Regions";

import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import PlusIcon from "components/svg/icons/PlusIcon";
import { CheckMarkIcon, QuestionMarkIcon, TrashIcon, UsersIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";


export default function Regions () {
  const params = useParams();

  const columnHelper = createColumnHelper();
  const [refreshData, setRefreshData] = useState<boolean>(false);  

  const [edit, setEdit] = useState<any>({});  
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [createModal, toggleCreateModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const toast = useToast();

  const columns = [  
    columnHelper.accessor((row: any) => row.color, {
      id: "color",
      cell: (info: any) => info.getValue() ? <span style={{display: "block",width: "25px", height: "25px", borderRadius: "50%", backgroundColor: info.getValue()}} /> : null,
      header: () => <span>Color</span>,      
      meta: {
        style: { width: '100px' },
      },
    }), 
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => <span style={{color:'#000'}}>{info.getValue()}</span>,
      header: () => <span>Name</span>,
    }), 
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        // Determine if "Deactivate" should be included based on row status
        const items = [
          { label: "Edit", onClick: () => openEditModal(info.row.original) }, 
          { label: "Delete", onClick: () => submitDelete(info.row.original), style : { color: "var(--theme-ui-colors-danger)"} }
        ];
    
        return (
          <Box>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];               

  const openEditModal = (edit: any) => {
    setEdit(edit);
    setEditModalOpen(true);
  };

  
  const closeEditModal = () => {
    setEdit(null);
    setEditModalOpen(false);
  }; 

  const submitUpdate = async (values: any, actions: any) => {   
    const update = {
      name : values.name,   
      color: values.color
    }  
    
    const res = await updateRegion({ data: { id: edit.id, clientId : params.id }, update });
    if(res?.id){ 
      toast.add("Region updated.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error updating region.","var(--theme-ui-colors-danger)");
    }          
    closeEditModal();        
  };  

  const UpdateSchema = Yup.object().shape({
    name: Yup.string().required("This field is required."),    
  });

  const toggleCreate = () => toggleCreateModal(!createModal);

  const submitCreate = async (values: any, { setErrors}: any) => {
  
    const create = {
      name: values.name,
      color: values.color
    };
    
    const res = await createRegion({ data: {clientId : params.id}, create });
    if(res?.id){ 
      toast.add("Region created.","var(--theme-ui-colors-success)");
      toggleCreateModal(false);
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error creating region.","var(--theme-ui-colors-danger)");
    }    
  };  

  const CreateSchema = Yup.object().shape({
    name: Yup.string().required("This field is required."),
  });

  const confirmDelete = async (del:any) => { 
    setConfirmSubmit(true);   
    const res = await deleteRegion({ data: { id: del.id, clientId : params.id } });
    if(res?.success){ 
      toast.add("Region deleted.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deleting region.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const cancelDeleteRegion = () => {
    setConfirmModal(false);
  }

  const submitDelete = async (del?:any) => {
    setConfirmModal(<BasicModal
      title="Delete region?"       
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      cancel_click={cancelDeleteRegion}
      cancel_text="No, cancel"  
      actions={<>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete(del)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Delete"}
        </BasicButton>
      </>}    
    >
      <P>Are you sure you want to delete <strong style={{color:"var(--theme-ui-colors-black)"}}>{del?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Regions"        
  }, []); 

  return (
    <>
      <Header></Header>      
      <Page title="Regions" subtitle="Manage company access.">                
        <TableData 
          api={getRegions} 
          columns={columns} 
          refresh={refreshData} 
          actions={[<BasicButton key="1" onClick={toggleCreate} $iconHeight="10px"><PlusIcon fill="#fff"/>Add Region</BasicButton>]} 
          title={"All Regions"}
          isFixed
          nothingFoundText="No regions found."
        />
      </Page>

      <Modal isOpen={editModalOpen} toggle={closeEditModal} centered={true} size="sm">
        <Formik initialValues={{
          ...(edit || {})
        }} onSubmit={submitUpdate} validationSchema={UpdateSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {              
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={closeEditModal}>
                  <UsersIcon fill={"000"} width="18px" height="18px" />
                  <Box>
                    <Box sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "Rubik",
                      color: "base_800"
                    }}>Edit region</Box>
                    <Box sx={{
                      fontSize: "12px",
                      fontFamily: "Poppins",
                      color: "dkgrey"
                    }}>Edit an existing region of your users.</Box>
                  </Box>
                </ModalHeader>
                <ModalBody>
                  <Flex sx={{display:'flex', alignItems:'center', gap:'20px'}}>
                    <TextInput 
                      name="name"
                      label="Region Name"
                      value={values.name}
                      onChange={(val: any) => setFieldValue("name",val)}
                      $errors={errors.name && submitCount > 0 ? errors.name : null}   
                      $customStyles={{flexGrow: "1"}}                                 
                    /> 
                    <div style={{width: "36px", height: "36px", borderRadius: "6px", backgroundColor: values.color, position:'relative', top:'-2px'}}></div>
                  </Flex>
                  <Box sx={{width:'100%', paddingBottom:'20px'}}>
                    <Box sx={{color:'#818181', fontFamily:'Poppins', fontSize:12, marginBottom:'8px'}}>Color</Box>
                    <SketchPicker color={values.color} onChangeComplete={(color: any) => setFieldValue('color', color.hex)} /> 
                  </Box>                                                     
                </ModalBody>
                <ModalFooter>
                  <BasicButton 
                    outline={true}               
                    styles={{margin:".25rem 20px .25rem 0"}}
                    type="button" 
                    onClick={closeEditModal} 
                    scheme="secondary"
                  >Cancel</BasicButton>
                  <BasicButton 
                    styles={{margin:".25rem 0 .25rem 0"}}
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save Region
                  </BasicButton>                 
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal> 
      <Modal isOpen={createModal} toggle={toggleCreate} centered={true} size="sm">
        <Formik initialValues={{color:'#000'}} onSubmit={submitCreate} validationSchema={CreateSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (              
            <Form>
              <ModalHeader toggle={toggleCreate}>
                <UsersIcon fill={"000"} width="18px" height="18px" />
                <Box>
                  <Box sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Rubik",
                    color: "base_800"
                  }}>Add region</Box>
                  <Box sx={{
                    fontSize: "12px",
                    fontFamily: "Poppins",
                    color: "dkgrey"
                  }}>Add a new region to organize your team.</Box>
                </Box>                
              </ModalHeader>
              <ModalBody>  
                <Flex sx={{display:'flex', alignItems:'center', gap:'20px'}}>                    
                  <TextInput 
                    name="name"
                    label="Region Name"
                    value={values.name}
                    onChange={(val: any) => setFieldValue("name",val)}
                    $errors={errors.name && submitCount > 0 ? errors.name : null}   
                    $customStyles={{flexGrow: "1"}}                                  
                  />   
                  <div style={{width: "36px", height: "36px", borderRadius: "6px", backgroundColor: values.color, position:'relative', top:'-2px'}}></div>
                </Flex>
                <Box sx={{width:'100%', paddingBottom:'20px'}}>
                  <Box sx={{color:'#818181', fontFamily:'Poppins', fontSize:12, marginBottom:'8px'}}>Color</Box>
                  <SketchPicker color={values.color} onChangeComplete={(color: any) => setFieldValue('color', color.hex)} /> 
                </Box>                               
              </ModalBody>
              <ModalFooter>
                <>
                  <BasicButton 
                    outline={true}               
                    styles={{margin:".25rem 20px .25rem 0"}}
                    type="button" 
                    onClick={toggleCreate} 
                    scheme="secondary"
                  >Cancel</BasicButton>
                  <BasicButton 
                    styles={{margin:".25rem 0 .25rem 0"}}
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  > 
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>} 
                    Save Region 
                  </BasicButton>                
                </>
              </ModalFooter>
            </Form>
          );
        }}            
        </Formik>
      </Modal> 
      {confirmModal}
    </>
  );
}
